import { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { XIcon } from '@heroicons/react/solid'
import { Button } from '../../../components'
import { recordCoverageEventSignature } from '../../../services/coverageEventServices'
import { mapCoverageEventError } from './coverageEventErrors'

const T = 'cycle_counts.events.reconciliation.signature'
// The platform's own limits (25 MB per file, images or PDF), checked here first so the operator
// hears about a wrong file before a 25 MB upload, and again by the platform, which is the authority.
export const MAX_EVIDENCE_BYTES = 25 * 1024 * 1024
// No SVG: an SVG can carry script, and evidence is opened in a new tab.
export const isAllowedEvidenceType = type => typeof type === 'string' && ((type.startsWith('image/') && type !== 'image/svg+xml') || type === 'application/pdf')
// The platform's bound on the note (the same one as the found-stock correction reason); a longer
// note is refused with a 422, so the field stops the operator at the limit instead.
export const MAX_NOTE_LENGTH = 500
// How long the form stays frozen on the read-back after a failure without a verdict. The read-back
// can hang for good (react-query pauses its retries while the browser reports offline): past this
// the form hands itself back with the error, and the read-back carries on in the background.
export const READ_BACK_WAIT_MS = 15 * 1000
const ACCEPT = 'image/*,application/pdf'
const formatSize = bytes => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`)

// The staff-recorded signature for a brand off the portal: a note naming who approved and through
// which channel, at least one evidence file, and the fingerprint of the proposal on screen sent
// silently — the platform refuses a fingerprint that no longer matches, so the operator can never
// record a signature against a proposal they did not see.
export const RecordSignatureDialog = ({ open, eventId, proposalFingerprint, onCancel, onRecorded, onRefused, onGateOff, onOutcomeUnknown, onBusyChange }) => {
  const { i18n } = useTranslation()
  const [note, setNote] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )
  useEffect(() => {
    if (!open) {
      setNote('')
      setFiles([])
      setError(null)
    }
  }, [open])

  const trimmedNote = note.trim()
  // One problem per file, the first found: type before size, so a 30 MB .docx reads as "not a PDF".
  const problemOf = file =>
    !isAllowedEvidenceType(file.type)
      ? i18n.t(`${T}.evidence_invalid_type`, { name: file.name })
      : file.size > MAX_EVIDENCE_BYTES
        ? i18n.t(`${T}.evidence_too_large`, { name: file.name })
        : null
  const problems = files.map(problemOf)
  const valid = trimmedNote.length > 0 && files.length > 0 && problems.every(p => p === null)

  const submit = async () => {
    if (!valid || busy) return
    setBusy(true)
    onBusyChange?.(true)
    setError(null)
    try {
      const result = await recordCoverageEventSignature(eventId, { proposal_fingerprint: proposalFingerprint, note: trimmedNote, evidence: files })
      // Awaited so the busy flag (and the panel's Cancelar) stays held until the caller has
      // adopted the returned header — otherwise the footer re-enables for the few ticks between
      // the response and the seeded header (Phase 6 eng review E-1).
      await onRecorded(result)
    } catch (err) {
      const mapped = mapCoverageEventError(err, i18n, { action: 'record_signature' })
      if (mapped.gateOff) {
        onGateOff?.()
        return
      }
      // A state conflict (already signed, no longer awaiting, stale fingerprint) or a lost
      // authority is nothing the form can fix: the pane refreshes and reports it outside.
      if (mapped.refetch || mapped.code === 'SETTLEMENT_AUTHORITY') {
        onRefused(mapped)
        return
      }
      // No verdict (network, 5xx): the signature may well have been recorded. The pane reads back,
      // and the form stays busy (Cancelar and Registrar firma disabled) until that read-back has
      // settled: if the event is signed now, this dialog goes away with the state it was rendered
      // for; if not, the error below is shown and the form re-enables. A failed read-back is the
      // pane's to report, so it never escapes this handler. The wait is bounded (READ_BACK_WAIT_MS):
      // a read-back that never settles must not freeze the modal for ever.
      if (mapped.code === 'UNEXPECTED') {
        let timer
        await Promise.race([
          Promise.resolve(onOutcomeUnknown?.()).catch(() => {}),
          new Promise(resolve => {
            timer = setTimeout(resolve, READ_BACK_WAIT_MS)
          }),
        ])
        clearTimeout(timer)
      }
      if (mounted.current) setError(mapped.message)
    } finally {
      // The busy flag is handed back to the pane whatever happened to this dialog (the pane
      // forwards it only while it is still on screen); only the local state needs a mount.
      onBusyChange?.(false)
      if (mounted.current) setBusy(false)
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={busy ? () => {} : onCancel}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg sm:w-full p-6 text-gray-600 my-3">
              <Dialog.Title className="text-lg text-center mb-2">{i18n.t(`${T}.dialog_title`)}</Dialog.Title>
              <p className="text-sm text-center text-gray-500 mb-1">{i18n.t(`${T}.dialog_body`)}</p>
              <p className="text-xs text-center text-amber-800 mb-4">{i18n.t(`${T}.may_refuse`)}</p>

              <label className="block text-sm text-gray-700 mb-3">
                <span className="block mb-1">{i18n.t(`${T}.note_label`)}</span>
                <textarea
                  className="w-full border-gray-300 rounded-md text-sm"
                  rows={3}
                  maxLength={MAX_NOTE_LENGTH}
                  value={note}
                  placeholder={i18n.t(`${T}.note_placeholder`)}
                  disabled={busy}
                  onChange={e => setNote(e.target.value)}
                />
              </label>

              <label className="block text-sm text-gray-700 mb-1">
                <span className="block mb-1">{i18n.t(`${T}.evidence_label`)}</span>
                {/* Uncontrolled on purpose: a file input cannot be given a value; the selection is
                    read out of the change event and kept in state. */}
                <input
                  type="file"
                  multiple
                  accept={ACCEPT}
                  disabled={busy}
                  className="block w-full text-sm"
                  onChange={e => setFiles(Array.from(e.target.files ?? []))}
                />
              </label>
              <p className="text-xs text-gray-500 mb-2">{i18n.t(`${T}.evidence_hint`)}</p>
              {files.length > 0 && (
                <ul className="text-sm mb-2 space-y-0.5">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className={problems[index] ? 'text-red-600' : 'text-gray-700'}>
                      {problems[index] ?? (
                        <>
                          {file.name} <span className="text-gray-400">({formatSize(file.size)})</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {error && (
                <p role="alert" className="text-sm text-red-600 mt-3 text-center">
                  {error}
                </p>
              )}

              <div className="mt-4 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel} disabled={busy}>
                  {i18n.t(`${T}.cancel`)}
                </Button>
                <Button
                  className="m-1"
                  type="primary"
                  onClick={submit}
                  disabled={busy || !valid}
                  loading={busy}
                  loading_label={i18n.t('cycle_counts.events.reconciliation.loading')}
                >
                  {i18n.t(`${T}.submit`)}
                </Button>
              </div>
              {/* Last in DOM order, absolutely positioned: the focus trap lands on the note, not here. */}
              <button
                type="button"
                aria-label={i18n.t(`${T}.close`)}
                className="absolute top-0 right-0 p-5 cursor-pointer disabled:opacity-50"
                disabled={busy}
                onClick={onCancel}
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
